import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import {
  ChatMessagePair,
  ChatConversationResponse,
  ConversationSummary,
} from '@services/receptionChatService'
import { receptionChatBus } from '@services/receptionChatBus'
import { useAuthStore } from '@store/useAuthStore'
import { invalidateNotifications } from '@utils/notificationQueries'

export interface ChatBubble {
  role: 'assistant' | 'user'
  content: string
  position: number
  pending?: boolean
}

export const CHAT_SEND_BLOCK_MS = 3000

/** Only used after a network timeout — not on API errors. */
const RECEPTION_POLL_INTERVAL_MS = 3000
const RECEPTION_POLL_MAX_ATTEMPTS = 5

const QUESTION_1_STATIC =
  'Para iniciar a receção preciso do nome do cliente (obrigatório). Email, telefone e NIF são opcionais. Se indicar um NIF com 9 dígitos, verifico se o cliente já existe e posso preencher dados; caso não exista, aviso que não foi encontrado.'

function isReceptionChatTimeout(error: unknown): boolean {
  const e = error as { code?: string; message?: string }
  return (
    e?.code === 'ECONNABORTED' ||
    String(e?.message ?? '').toLowerCase().includes('timeout') ||
    String(e?.message ?? '').includes('499')
  )
}

function extractApiErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data
  const message = data?.message
  return message && String(message).trim() ? String(message).trim() : null
}

function hasWorkOrderSuccessMessage(synced: ChatConversationResponse): boolean {
  return (
    synced.messages?.some((m) =>
      (m.question?.includes('Folha de obra criada com sucesso') ?? false)
    ) ?? false
  )
}

interface SyncBaseline {
  pairCount: number
  workOrderId?: number
  workOrderNumber?: string
  pendingText: string
  expectWorkOrder: boolean
}

function countMessagePairs(messages: ChatMessagePair[] | undefined): number {
  return Array.isArray(messages) ? messages.length : 0
}

function hasConversationProgress(synced: ChatConversationResponse, baseline: SyncBaseline): boolean {
  if (baseline.expectWorkOrder) {
    return isWorkOrderReady(synced, baseline) || hasWorkOrderSuccessMessage(synced)
  }

  const pairs = countMessagePairs(synced.messages)
  if (pairs > baseline.pairCount) return true
  if (synced.workOrderId != null && synced.workOrderId !== baseline.workOrderId) return true
  if (synced.workOrderNumber && synced.workOrderNumber !== baseline.workOrderNumber) return true
  if (synced.status === 1) return true

  if (baseline.pendingText) {
    const saved = synced.messages?.some(
      (m) =>
        m.answer?.trim() === baseline.pendingText.trim() ||
        (m.answer?.includes(baseline.pendingText.trim()) ?? false)
    )
    if (saved) return true
  }

  return hasWorkOrderSuccessMessage(synced)
}

function isWorkOrderReady(synced: ChatConversationResponse, baseline: SyncBaseline): boolean {
  if (!baseline.expectWorkOrder) return true
  return Boolean(synced.workOrderId && synced.workOrderNumber)
}

async function syncConversationAfterTimeout(
  conversationId: number,
  baseline: SyncBaseline
): Promise<ChatConversationResponse | null> {
  for (let attempt = 0; attempt < RECEPTION_POLL_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, RECEPTION_POLL_INTERVAL_MS))
    }
    try {
      const synced = await receptionChatBus.getConversation(conversationId)
      if (hasConversationProgress(synced, baseline)) {
        return synced
      }
    } catch {
      /* retry */
    }
  }
  return null
}

export function useReceptionChat(conversationId?: number) {
  const [convId, setConvId] = useState<number | undefined>(conversationId)
  const [messages, setMessages] = useState<ChatBubble[]>([])
  const [followUp, setFollowUp] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isLoadingConversation, setIsLoadingConversation] = useState(false)
  const [isBackgroundSync, setIsBackgroundSync] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [pendingUserText, setPendingUserText] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<ConversationSummary[]>([])
  const [workOrderId, setWorkOrderId] = useState<number | undefined>(undefined)
  const [workOrderNumber, setWorkOrderNumber] = useState<string | undefined>(undefined)
  const [status, setStatus] = useState<number | undefined>(undefined)
  const [isScheduling, setIsScheduling] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<string | undefined>(undefined)
  const [hasInitialized, setHasInitialized] = useState(conversationId !== undefined)

  const { user, isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const operationIdRef = useRef(0)
  const convIdRef = useRef(convId)
  const serverPairCountRef = useRef(0)
  convIdRef.current = convId

  const applyConversationResponse = useCallback(
    (data: ChatConversationResponse, previousWorkOrderId?: number, previousWorkOrderNumber?: string) => {
      setConvId(data.conversationId)
      const messagesArray = Array.isArray(data.messages) ? data.messages : []
      setMessages(buildBubbles(messagesArray))
      setFollowUp(buildFollowUpText(messagesArray, data.followUp))
      setWorkOrderId(data.workOrderId)
      setWorkOrderNumber(data.workOrderNumber)
      if (data.status != null) setStatus(data.status)
      if (data.scheduling != null) setIsScheduling(Boolean(data.scheduling))
      if (data.scheduledAt) setScheduledAt(data.scheduledAt)
      setPendingUserText(null)
      serverPairCountRef.current = countMessagePairs(messagesArray)

      if (
        (!previousWorkOrderId && data.workOrderId) ||
        (!previousWorkOrderNumber && data.workOrderNumber)
      ) {
        queryClient.invalidateQueries('workOrders')
        queryClient.invalidateQueries('mechanicPendingWorkOrders')
        invalidateNotifications(queryClient)
        queryClient.refetchQueries('workOrders')
        queryClient.refetchQueries('mechanicPendingWorkOrders')
      }
    },
    [queryClient]
  )

  const loadConversation = useCallback(
    async (id: number) => {
      const opId = ++operationIdRef.current
      setIsLoadingConversation(true)
      setSyncError(null)
      setPendingUserText(null)
      setIsBackgroundSync(false)
      try {
        const data = await receptionChatBus.getConversation(id)
        if (opId !== operationIdRef.current) return
        applyConversationResponse(data)
      } catch (error) {
        console.error('Erro ao carregar conversa:', error)
        setMessages([])
        setFollowUp(null)
        setWorkOrderId(undefined)
        setWorkOrderNumber(undefined)
        setStatus(undefined)
        setIsScheduling(false)
        setScheduledAt(undefined)
      } finally {
        if (opId === operationIdRef.current) {
          setIsLoadingConversation(false)
        }
      }
    },
    [applyConversationResponse]
  )

  useEffect(() => {
    if (!hasInitialized && conversationId === undefined && convId === undefined) {
      setHasInitialized(true)
      setMessages([
        {
          role: 'assistant',
          content: QUESTION_1_STATIC,
          position: 0,
        },
      ])
      setFollowUp(null)
    }
  }, [hasInitialized, conversationId, convId])

  useEffect(() => {
    if (conversationId !== undefined && conversationId !== convId) {
      setHasInitialized(true)
      void loadConversation(conversationId)
    }
  }, [conversationId, convId, loadConversation])

  const loadDrafts = useCallback(async () => {
    if (!isAuthenticated || user?.workshopCompanyId == null) {
      setDrafts([])
      return
    }
    try {
      const list = await receptionChatBus.listDrafts()
      setDrafts(list || [])
    } catch (err) {
      console.error('Erro ao carregar conversas em rascunho', err)
      setDrafts([])
    }
  }, [isAuthenticated, user?.workshopCompanyId])

  const startScheduling = useCallback(
    async (scheduledAtIso: string) => {
      const opId = ++operationIdRef.current
      setIsSending(true)
      setSyncError(null)
      const unblockTimer = window.setTimeout(() => setIsSending(false), CHAT_SEND_BLOCK_MS)
      let slowTimer: number | undefined

      try {
        const createdBy = user?.email || user?.name
        slowTimer = window.setTimeout(() => {
          if (opId === operationIdRef.current) setIsBackgroundSync(true)
        }, CHAT_SEND_BLOCK_MS)

        const data = await receptionChatBus.startScheduling(scheduledAtIso, createdBy)
        if (opId !== operationIdRef.current) return
        applyConversationResponse(data)
        setIsScheduling(true)
        setScheduledAt(data.scheduledAt ?? scheduledAtIso)
        setHasInitialized(true)
        void loadDrafts()
        return data.conversationId
      } catch (error) {
        console.error('Erro ao iniciar marcação:', error)
        const apiMessage = extractApiErrorMessage(error)
        if (opId === operationIdRef.current) {
          setSyncError(apiMessage ?? 'Não foi possível iniciar a marcação. Tente novamente.')
        }
        throw error
      } finally {
        if (slowTimer) window.clearTimeout(slowTimer)
        window.clearTimeout(unblockTimer)
        if (opId === operationIdRef.current) {
          setIsSending(false)
          setIsBackgroundSync(false)
        }
      }
    },
    [user?.email, user?.name, loadDrafts, applyConversationResponse]
  )

  const deleteDraft = useCallback(
    async (conversationIdToDelete?: number) => {
      if (!conversationIdToDelete) return
      const opId = ++operationIdRef.current
      setIsSending(true)
      setSyncError(null)
      try {
        await receptionChatBus.deleteDraft(conversationIdToDelete)
        if (opId !== operationIdRef.current) return

        if (convIdRef.current === conversationIdToDelete) {
          // If we deleted the currently open draft, reset UI to a new conversation
          setConvId(undefined)
          setMessages([])
          setFollowUp(null)
          setWorkOrderId(undefined)
          setWorkOrderNumber(undefined)
          setStatus(undefined)
          setIsScheduling(false)
          setScheduledAt(undefined)
          setHasInitialized(false)
          setPendingUserText(null)
        }
        await loadDrafts()
      } catch (error) {
        console.error('Erro ao eliminar rascunho:', error)
        const apiMessage = extractApiErrorMessage(error)
        if (opId === operationIdRef.current) {
          setSyncError(apiMessage ?? 'Não foi possível eliminar o rascunho. Tente novamente.')
        }
        throw error
      } finally {
        if (opId === operationIdRef.current) {
          setIsSending(false)
          setIsBackgroundSync(false)
        }
      }
    },
    [loadDrafts]
  )

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || isSending || isBackgroundSync) return

      const opId = ++operationIdRef.current
      const previousWorkOrderId = workOrderId
      const previousWorkOrderNumber = workOrderNumber

      const lastAssistant = [...messages]
        .filter((m) => m.role === 'assistant' && !m.pending)
        .sort((a, b) => a.position - b.position)
        .pop()
      const expectWorkOrder =
        /tipo de serviço de manutenção/i.test(lastAssistant?.content ?? '') ||
        /informações adicionais/i.test(lastAssistant?.content ?? '')

      const baseline: SyncBaseline = {
        pairCount: serverPairCountRef.current,
        workOrderId: previousWorkOrderId,
        workOrderNumber: previousWorkOrderNumber,
        pendingText: trimmed,
        expectWorkOrder,
      }

      setPendingUserText(trimmed)
      setFollowUp(null)
      setSyncError(null)
      setIsSending(true)

      const unblockTimer = window.setTimeout(() => {
        if (opId === operationIdRef.current) setIsSending(false)
      }, CHAT_SEND_BLOCK_MS)

      const slowTimer = window.setTimeout(() => {
        if (opId === operationIdRef.current) setIsBackgroundSync(true)
      }, CHAT_SEND_BLOCK_MS)

      try {
        const createdBy = user?.email || user?.name
        const data = convId
          ? await receptionChatBus.sendMessage(convId, trimmed, createdBy)
          : await receptionChatBus.start(trimmed, createdBy)

        if (opId !== operationIdRef.current) return data.conversationId

        applyConversationResponse(data, previousWorkOrderId, previousWorkOrderNumber)
        void loadDrafts()
        return data.conversationId
      } catch (error) {
        const targetConvId = convIdRef.current ?? convId
        const timedOut = isReceptionChatTimeout(error)
        const apiMessage = extractApiErrorMessage(error)

        if (timedOut && targetConvId) {
          if (opId === operationIdRef.current) {
            setIsBackgroundSync(true)
            setSyncError(
              'O servidor ainda está a processar. A sincronizar a conversa (pode demorar alguns segundos)…'
            )
          }
          const synced = await syncConversationAfterTimeout(targetConvId, baseline)
          if (opId === operationIdRef.current && synced) {
            applyConversationResponse(synced, previousWorkOrderId, previousWorkOrderNumber)
            void loadDrafts()
            setSyncError(null)
            setIsBackgroundSync(false)
            return synced.conversationId
          }
          if (opId === operationIdRef.current) {
            setSyncError(
              baseline.expectWorkOrder
                ? 'A resposta pode ter sido guardada, mas a folha de obra ainda não apareceu. Atualize a conversa ou tente novamente.'
                : 'Não foi possível confirmar a resposta. Atualize a conversa ou tente novamente.'
            )
            setIsBackgroundSync(false)
          }
          return targetConvId
        }

        if (opId === operationIdRef.current) {
          setSyncError(
            apiMessage ??
              'Não foi possível enviar a mensagem. Verifique a ligação e tente novamente.'
          )
          setPendingUserText(null)
          if (targetConvId) {
            try {
              const synced = await receptionChatBus.getConversation(targetConvId)
              if (opId === operationIdRef.current) {
                applyConversationResponse(synced, previousWorkOrderId, previousWorkOrderNumber)
              }
            } catch {
              /* keep error visible */
            }
          }
        }

        console.error('Erro ao enviar mensagem:', error)
        throw error
      } finally {
        window.clearTimeout(unblockTimer)
        window.clearTimeout(slowTimer)
        if (opId === operationIdRef.current) {
          setIsSending(false)
          setIsBackgroundSync(false)
        }
      }
    },
    [
      convId,
      user?.email,
      user?.name,
      loadDrafts,
      workOrderId,
      workOrderNumber,
      isSending,
      isBackgroundSync,
      messages,
      applyConversationResponse,
    ]
  )

  const concludeConversation = useCallback(
    async (id?: number) => {
      if (!id) return
      const opId = ++operationIdRef.current
      setIsSending(true)
      try {
        const conversation = await receptionChatBus.getConversation(id)

        if (conversation.workOrderId && conversation.workOrderNumber) {
          applyConversationResponse(conversation)
          await new Promise((resolve) => setTimeout(resolve, 2000))
        }

        await receptionChatBus.updateStatus(id, 1, user?.email || user?.name)

        setConvId(undefined)
        setMessages([])
        setFollowUp(null)
        setWorkOrderId(undefined)
        setWorkOrderNumber(undefined)
        setHasInitialized(false)
        setPendingUserText(null)

        try {
          const createdBy = user?.email || user?.name
          const data = await receptionChatBus.start('', createdBy)
          if (opId === operationIdRef.current) {
            applyConversationResponse(data)
            setHasInitialized(true)
          }
        } catch (initError) {
          console.error('Erro ao reiniciar conversa após concluir:', initError)
        }
        void loadDrafts()
      } catch (error) {
        console.error('Erro ao concluir conversa:', error)
        setConvId(undefined)
        setMessages([])
        setFollowUp(null)
        setWorkOrderId(undefined)
        setWorkOrderNumber(undefined)
        setHasInitialized(false)
        setPendingUserText(null)
      } finally {
        if (opId === operationIdRef.current) {
          setIsSending(false)
          setIsBackgroundSync(false)
        }
      }
    },
    [user?.email, user?.name, loadDrafts, applyConversationResponse]
  )

  const displayMessages = useMemo(() => {
    const items: ChatBubble[] = [...messages]

    if (pendingUserText) {
      const alreadyOnServer = items.some(
        (m) => m.role === 'user' && m.content.trim() === pendingUserText.trim() && !m.pending
      )
      if (!alreadyOnServer) {
        const maxPos = items.length > 0 ? Math.max(...items.map((m) => m.position)) : 0
        items.push({
          role: 'user',
          content: pendingUserText,
          position: maxPos + 0.9,
          pending: true,
        })
      }
    }

    const inFlight = Boolean(pendingUserText) || isSending || isBackgroundSync
    if (followUp && !inFlight) {
      const normalizedFollowUp = followUp.trim()
      const alreadyShown = items.some(
        (m) => m.role === 'assistant' && m.content.trim() === normalizedFollowUp
      )
      if (!alreadyShown) {
        const maxPos = items.length > 0 ? Math.max(...items.map((m) => m.position)) : 0
        items.push({
          role: 'assistant',
          content: followUp,
          position: maxPos + 0.95,
        })
      }
    }

    return items.sort((a, b) => a.position - b.position)
  }, [messages, followUp, pendingUserText, isSending, isBackgroundSync])

  const isLoading = isSending || isLoadingConversation

  return {
    conversationId: convId,
    messages: displayMessages,
    sendMessage,
    isLoading,
    isSending,
    isLoadingConversation,
    isBackgroundSync,
    syncError,
    loadConversation,
    concludeConversation,
    loadDrafts,
    drafts,
    workOrderId,
    workOrderNumber,
    followUp,
    startScheduling,
    isScheduling,
    scheduledAt,
    status,
    deleteDraft,
    resetToNew: () => {
      operationIdRef.current += 1
      setConvId(undefined)
      setFollowUp(null)
      setWorkOrderId(undefined)
      setWorkOrderNumber(undefined)
      setStatus(undefined)
      setIsScheduling(false)
      setScheduledAt(undefined)
      setHasInitialized(false)
      setPendingUserText(null)
      setSyncError(null)
      setIsBackgroundSync(false)
      setIsSending(false)
      setMessages([
        {
          role: 'assistant',
          content: QUESTION_1_STATIC,
          position: 0,
        },
      ])
      setTimeout(() => {
        setHasInitialized(true)
      }, 100)
    },
  }
}

function buildBubbles(messages: ChatMessagePair[]): ChatBubble[] {
  const result: ChatBubble[] = []
  if (!Array.isArray(messages)) {
    return result
  }

  const sortedMessages = [...messages]
    .filter((m) => m != null)
    .sort((a, b) => (a.position || 0) - (b.position || 0))

  sortedMessages.forEach((m) => {
    const pos = m.position || 0
    if (m.question && typeof m.question === 'string' && m.question.trim()) {
      result.push({ role: 'assistant', content: m.question.trim(), position: pos })
    }
    if (m.answer && typeof m.answer === 'string' && m.answer.trim()) {
      result.push({ role: 'user', content: m.answer.trim(), position: pos + 0.001 })
    }
  })
  return result.sort((a, b) => a.position - b.position)
}

function buildFollowUpText(
  _messages: ChatMessagePair[],
  followUp?: { prefix: string; questions: string[] } | null
) {
  if (!followUp || !followUp.questions || followUp.questions.length === 0) return null
  if (followUp.questions.length === 1) {
    return followUp.questions[0]
  }
  const bulletList = followUp.questions.map((q) => `• ${q}`).join('\n')
  if (followUp.prefix && followUp.prefix !== 'null') {
    return `${followUp.prefix}\n${bulletList}`
  }
  return bulletList
}
